import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { rpc } from '@/lib/rpc';
import { supabase } from '@/lib/supabase';
import { profileKeys } from '@/features/auth/useProfile';
import type { Profile, UserRole } from '@/types/app';

export const userKeys = {
  all: ['users'] as const,
  list: (includeArchived: boolean) => ['users', 'list', includeArchived] as const,
};

// Must stay a single string literal: Supabase infers the row type from the literal,
// and a concatenated expression widens to `string`, which breaks that inference.
// prettier-ignore
const PROFILE_COLUMNS = 'id, full_name, email, phone, avatar_url, role, team_id, manager_id, is_active, accepts_new_leads, capacity_max, areas, languages, status, last_login_at, created_at, updated_at';

export function useUsers(includeArchived = false) {
  return useQuery({
    queryKey: userKeys.list(includeArchived),
    queryFn: async (): Promise<Profile[]> => {
      let query = supabase.from('profiles').select(PROFILE_COLUMNS).order('full_name');
      if (!includeArchived) query = query.eq('status', 'active');

      const { data, error } = await query;
      if (error) throw error;
      return data ?? [];
    },
  });
}

/** Invalidate both the user list and the signed-in user's own profile: an admin can
 *  change their own team, and the shell reads role from the profile query. */
function useAdminInvalidation() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: userKeys.all });
    void queryClient.invalidateQueries({ queryKey: profileKeys.all });
  };
}

export interface CreateUserInput {
  email: string;
  password: string;
  full_name: string;
  role: UserRole;
  team_id?: string | null;
  phone?: string | null;
}

/**
 * Account creation runs through the admin-create-user Edge Function, because it needs
 * the Auth Admin API and therefore the service_role key, which must never reach the
 * browser. The function re-checks that the caller is an admin before doing anything.
 */
export function useCreateUser() {
  const invalidate = useAdminInvalidation();

  return useMutation({
    mutationFn: async (input: CreateUserInput): Promise<{ user_id: string }> => {
      // Narrowed explicitly: without generated database types the client is
      // parameterised as `any`, so the invoke() response arrives untyped.
      const { data, error } = (await supabase.functions.invoke('admin-create-user', {
        body: input,
      })) as { data: { user_id?: string; error?: string } | null; error: Error | null };

      if (error) throw error;
      if (!data?.user_id) throw new Error(data?.error ?? 'create_failed');
      return { user_id: data.user_id };
    },
    onSuccess: invalidate,
  });
}

export function useSetUserRole() {
  const invalidate = useAdminInvalidation();

  return useMutation({
    mutationFn: async ({
      userId,
      role,
      reason,
    }: {
      userId: string;
      role: UserRole;
      reason?: string;
    }) => {
      await rpc('admin_set_role', { p_user: userId, p_role: role, p_reason: reason ?? null });
    },
    onSuccess: invalidate,
  });
}

export function useSetUserTeam() {
  const invalidate = useAdminInvalidation();

  return useMutation({
    mutationFn: async ({
      userId,
      teamId,
      reason,
    }: {
      userId: string;
      teamId: string | null;
      reason?: string;
    }) => {
      await rpc('admin_set_team', {
        p_user: userId,
        p_team: teamId,
        p_manager: null,
        p_reason: reason ?? null,
      });
    },
    onSuccess: invalidate,
  });
}

export function useSetUserActive() {
  const invalidate = useAdminInvalidation();

  return useMutation({
    mutationFn: async ({
      userId,
      active,
      reason,
    }: {
      userId: string;
      active: boolean;
      reason?: string;
    }) => {
      await rpc('admin_set_active', {
        p_user: userId,
        p_active: active,
        p_reason: reason ?? null,
      });
    },
    onSuccess: invalidate,
  });
}
